import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import { queryCreate, queryGet } from './fetch';
import { useAccountById, useMe } from './auth';
import { getLocale } from '@app/i18n';

export const participantConsent = ['privacy-policy', 'terms'];
export const participationRulesConsent = 'participation-rules';

export interface Consent {
  id?: string;
  documentId: string;
  date: Date;
}

export interface ConsentConfig {
  id: string;
  name: string;
  url: string;
}

export enum ConsentName {
  CLIENT_STUDY_TERMS = 'general-terms-and-conditions-of-service',

  PARTICIPANT_RULES = 'participation-rules',
  PARTICIPANT_PRIVACY = 'participants-privacy-policy',
  PARTICIPANT_TERMS = 'terms-and-conditions-participant',
}

type ConsentConfigs = {
  [name in ConsentName]: ConsentConfig;
};

type LegalDocument = {
  name: string;
  enName?: string;
  locale: string;
  publishedAt: string;
  id: string;
};

export const useAcceptConsent = () => {
  const queryClient = useQueryClient();
  return useMutation<Consent, undefined, Consent>(
    queryCreate(`/v2/auth/me/consents`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['account']);
        queryClient.invalidateQueries(['auth', 'me']);
      },
    },
  );
};

export const useCurrentDocumentsVersion = () => {
  return useQuery<LegalDocument[]>(
    ['document-versions'],
    queryGet('/v2/auth/document-versions'),
  );
};

export const useAllDocumentsVersion = () => {
  return useQuery<LegalDocument[]>(
    ['document-versions', 'all'],
    queryGet('/v2/auth/document-versions?all=true'),
  );
};

export const useConsentConfigs = () => {
  const { i18n } = useTranslation('common');
  const locale = getLocale().substring(0, 2).toLowerCase();
  const { data: documentVersions } = useCurrentDocumentsVersion();

  return useMemo(() => {
    const configs: Record<string, ConsentConfig> = {};
    for (const consentKey in ConsentName) {
      const consentName = ConsentName[consentKey as keyof typeof ConsentName];
      const doc =
        documentVersions?.find(
          (elem) => elem.locale === locale && elem.enName === consentName,
        ) ||
        documentVersions?.find(
          (elem) => elem.locale === 'en' && elem.name === consentName,
        );
      if (!doc) {
        continue;
      }
      const name = consentName as string;
      configs[name] = {
        id: doc.id,
        name,
        url: `https://www.tandemz.io/legal/${doc.name}`,
      };
    }
    return configs as ConsentConfigs;
  }, [documentVersions, locale]);
};

const hasRequiredConsentsInDocuments = (
  requiredConsents: ConsentName[],
  documents: LegalDocument[],
  currentConsents: Consent[],
) => {
  return requiredConsents.every((name) => {
    const res = documents.some((doc) => {
      if (doc.name !== name && doc.enName !== name) {
        return false;
      }
      return currentConsents.some((consent) => consent.documentId === doc.id);
    });

    return res;
  });
};

export const useHasConsents = (consents: ConsentName[]) => {
  const { data: me } = useMe(true);
  const { data: account } = useAccountById(me?.id);
  const { data: documents } = useCurrentDocumentsVersion();

  return useMemo(() => {
    if (!documents || !account || !account.consents) {
      return false;
    }
    return hasRequiredConsentsInDocuments(
      consents,
      documents,
      account.consents,
    );
  }, [me, documents]);
};

export const hasConsents = async (
  requiredConsents: ConsentName[],
  consents: Consent[],
) => {
  const documents = await queryGet<LegalDocument[]>(
    '/v2/auth/document-versions',
  )();
  return hasRequiredConsentsInDocuments(requiredConsents, documents, consents);
};

export const useConsentUrl = (name: ConsentName) => {
  const configs = useConsentConfigs();
  const config = configs[name];
  return config?.url || 'https://www.tandemz.io';
};

export const useGetConsentUrl = () => {
  const configs = useConsentConfigs();
  return useCallback(
    (name: ConsentName) => {
      const config = configs[name];
      return config?.url || 'https://www.tandemz.io';
    },
    [configs],
  );
};

export const useGetConsentConfig = () => {
  const configs = useConsentConfigs();
  return useCallback(
    (name: ConsentName) => {
      const config = configs[name];
      return { ...config, url: config?.url || 'https://www.tandemz.io' };
    },
    [configs],
  );
};
